import React, {
  useContext, useEffect, useMemo, useState,
} from 'react';
import { HeadingBanner } from 'anf-core-react';
import { useMutation } from '@apollo/client';
import CheckoutPageContext from '../../../context/CheckoutPageContext';
import AddressForm from '../../Common/AddressForm/AddressForm';
import Tmnt from '../../Tmnt/Tmnt';
import AddressSelectionModal from './AddressSelectionModal/AddressSelectionModal';
import SavedAddress from './SavedAddress/SavedAddress';
import SHIPPING_SPEED_UPDATE_MUTATION from '../../../gql/shippingSpeed.gql';
import useLog from '../../useLog/useLog';
import $document from '../../../tools/document';

const SHIPPING_VIEW = {
  SHIPPING_FORM: 'SHIPPING_FORM',
  SAVED_ADDRESS: 'SAVED_ADDRESS',
};

export default function ShippingAddressContainer() {
  const [visibleView, setVisibleView] = useState(SHIPPING_VIEW.SAVED_ADDRESS);
  const [openAddressSelectionModal, setOpenAddressSelectionModal] = useState(false);

  const {
    checkoutPageState,
    setCheckoutPageState,
    setIsUpdating,
  } = useContext(CheckoutPageContext);

  const {
    textFor,
    addressBook,
    shippingAddressConfig,
    shipOrPickup,
    addressyApiKey,
  } = checkoutPageState;

  const logger = useLog('checkout.updateAddressType');

  const [updateAddressMutation] = useMutation(SHIPPING_SPEED_UPDATE_MUTATION, {
    onCompleted: (response) => {
      logger.debug('SHIPPING_SPEED_UPDATE_MUTATION_FROM_UPDATE_ADDRESS_RESULT', response);
      if (response.updateShippingSpeed) {
        setCheckoutPageState((currentState) => ({
          ...currentState, ...response.updateShippingSpeed,
        }));
      }
      setIsUpdating(false);
      setVisibleView(SHIPPING_VIEW.SAVED_ADDRESS);
    },
    onError: (err) => {
      setIsUpdating(false);
      logger.error(`ERR: ADDRESS_UPDATE_MUTATION: ${JSON.stringify(err)}`);
    },
  });

  const setModalVisibility = (isVisible) => {
    setOpenAddressSelectionModal(isVisible);
    $document.documentElement.setAttribute('data-scroll-lock', 'false');
  };

  const onAddNewAddress = () => {
    setModalVisibility(false);
    setVisibleView(SHIPPING_VIEW.SHIPPING_FORM);

    const event = new Event('clearShippingAddress');
    window.dispatchEvent(event);
  };

  const onUseThisAddress = (selectedAddress, isSelectedAddress) => {
    if (isSelectedAddress) {
      setModalVisibility(false);
      return;
    }
    setIsUpdating(true);
    setModalVisibility(false);
    updateAddressMutation({
      variables: {
        shippingAddress: {
          address1: selectedAddress.address1,
          address2: selectedAddress.address2,
          addressId: selectedAddress.addressId,
          addressType: selectedAddress.addressType,
          city: selectedAddress.city,
          country: selectedAddress.country,
          displayType: selectedAddress.displayType,
          firstName: selectedAddress.firstName,
          isBillingAllowed: selectedAddress.isBillingAllowed,
          isDefaultBilling: selectedAddress.isDefaultBilling,
          isDefaultShipping: selectedAddress.isDefaultShipping,
          isShippingAllowed: selectedAddress.isShippingAllowed,
          lastName: selectedAddress.lastName,
          orgName: selectedAddress.orgName,
          pickUpStoreId: selectedAddress.pickUpStoreId,
          poBox: selectedAddress.poBox,
          postalCode: selectedAddress.postalCode,
          state: selectedAddress.state,
          subType: selectedAddress.subType,
          title: selectedAddress.title,
        },
      },
    });
  };

  useEffect(() => {
    if (addressBook && !addressBook.defaultShipping) {
      setVisibleView(SHIPPING_VIEW.SHIPPING_FORM);
    }
  }, [addressBook]);

  const addressConfigData = useMemo(() => {
    if (!shippingAddressConfig || shippingAddressConfig.addressFields.length === 0) {
      return null;
    }

    return {
      id: 'shipping',
      fields: shippingAddressConfig.addressFields.map((field) => {
        const label = textFor?.[field.label]
          ?? { key: field.label, value: field.label };
        const error = textFor?.[field.errorMessage]
          ?? { key: field.errorMessage, value: field.errorMessage };
        return {
          ...field,
          label,
          error,
        };
      }),
    };
  }, [shippingAddressConfig, textFor]);

  const onChangeAddress = () => {
    setOpenAddressSelectionModal(true);
  };

  const isSavedAddressVisible = useMemo(
    () => visibleView === SHIPPING_VIEW.SHIPPING_FORM
      && addressBook
      && addressBook.addresses.length > 0,
    [addressBook, visibleView],
  );

  const savedAddress = useMemo(() => {
    if (!addressBook) return null;

    return addressBook.addresses.find((item) => item.addressId === addressBook?.defaultShipping);
  }, [addressBook]);

  const errorTmnts = useMemo(() => ({
    firstNameMissing: textFor?.firstNameMissing,
    lastNameMissing: textFor?.lastNameMissing,
    nameMissing: textFor?.nameMissing,
    address1ErrorMessage: textFor?.address1ErrorMessage,
    cityMissing: textFor?.cityMissing,
    postalUSCodeErrorMessage: textFor?.postalUSCodeErrorMessage,
    postalCACodeErrorMessage: textFor?.postalCACodeErrorMessage,
    postalCodeErrorMessage: textFor?.postalCodeErrorMessage,
  }), [textFor]);

  if (!addressConfigData) return null;

  return (
    <div className="shipping-address-container">
      <HeadingBanner variant="boxed">
        <h2>
          <Tmnt tmnt={textFor?.shippingAddress} />
        </h2>
        {isSavedAddressVisible && (
          <button
            className="button link-button"
            onClick={onChangeAddress}
            type="button"
          >
            {textFor?.savedAddress?.value}
          </button>
        )}
      </HeadingBanner>
      {visibleView === SHIPPING_VIEW.SHIPPING_FORM && (
        <AddressForm
          addressConfig={addressConfigData}
          type="shipping"
          fieldInFix="homeDelivery"
          countryCode={shipOrPickup?.country}
          addressyEnabled={!!shippingAddressConfig?.isAddressyEnabled}
          addressyKey={addressyApiKey}
          manualEntryItemLabel={textFor?.addressyManualEntry}
          errorTmnts={errorTmnts}
        />
      )}

      {visibleView === SHIPPING_VIEW.SAVED_ADDRESS && (
        <SavedAddress
          savedAddress={savedAddress}
          textFor={{
            change: textFor?.change,
          }}
          onChangeAddress={onChangeAddress}
        />
      )}

      {openAddressSelectionModal
        && (
          <AddressSelectionModal
            addressData={checkoutPageState?.addressBook?.addresses}
            textFor={checkoutPageState?.textFor}
            shippingAddress={checkoutPageState?.shippingAddress}
            isOpen={openAddressSelectionModal}
            onAddNewAddress={onAddNewAddress}
            setModalVisibility={setModalVisibility}
            onUseThisAddress={onUseThisAddress}
          />
        )}
    </div>
  );
}
